import { formatDayShort } from "./format";
import type {
  PublicTrackingStatus,
  TrackingEventStatus,
  TrackingOverallStatus,
} from "./types";

/** The four stepper phases (the terminal returned/undeliverable live on `status`). */
export type LifecyclePhase =
  | "pre_transit"
  | "in_transit"
  | "out_for_delivery"
  | "delivered";

export type LifecycleStepState = "complete" | "current" | "upcoming";

export interface LifecycleStep {
  key: LifecyclePhase;
  label: string;
  /** "Jun 7" — null when no scan has reached this phase yet. */
  dateLabel: string | null;
  state: LifecycleStepState;
}

export interface TrackingLifecycle {
  status: TrackingOverallStatus;
  steps: LifecycleStep[];
}

const PHASE_ORDER: LifecyclePhase[] = [
  "pre_transit",
  "in_transit",
  "out_for_delivery",
  "delivered",
];

const PHASE_LABEL: Record<LifecyclePhase, string> = {
  pre_transit: "Accepted",
  in_transit: "In Transit",
  out_for_delivery: "Out for Delivery",
  delivered: "Delivered",
};

/** Map the backend overall status onto a stepper phase index. */
function statusToIndex(status: TrackingOverallStatus): number {
  switch (status) {
    case "pre_transit":
      return 0;
    case "in_transit":
      return 1;
    case "out_for_delivery":
      return 2;
    case "delivered":
      return 3;
    // returned / undeliverable are terminal mid-stream — reflect transit
    // progress on the bar; the real status is shown in the hero.
    default:
      return 1;
  }
}

function eventPhase(status: TrackingEventStatus): LifecyclePhase | null {
  switch (status) {
    case "processed":
    case "in_transit":
      return "in_transit";
    case "out_for_delivery":
      return "out_for_delivery";
    case "delivered":
      return "delivered";
    default:
      return null;
  }
}

/**
 * Build the 4-step delivery lifecycle from the backend-derived `status` plus
 * the `events` timeline. Pure + SSR-safe. The backend owns the overall status
 * (including time-boxed inferred delivery); we only derive per-step dates.
 */
export function deriveTrackingLifecycle(
  data: PublicTrackingStatus,
): TrackingLifecycle {
  const currentIndex = statusToIndex(data.status);

  const earliestByPhase: Partial<Record<LifecyclePhase, string>> = {};
  for (const event of data.events) {
    const phase = eventPhase(event.status);
    if (phase && !earliestByPhase[phase]) {
      earliestByPhase[phase] = event.timestamp;
    }
  }

  const dateFor = (phase: LifecyclePhase): string | null => {
    switch (phase) {
      case "delivered":
        return data.delivered_date ?? earliestByPhase.delivered ?? null;
      case "out_for_delivery":
        return earliestByPhase.out_for_delivery ?? null;
      case "in_transit":
        return earliestByPhase.in_transit ?? null;
      default:
        // "Accepted" has no acceptance scan — use the first event as a proxy.
        return data.events[0]?.timestamp ?? null;
    }
  };

  const steps: LifecycleStep[] = PHASE_ORDER.map((key, index) => ({
    key,
    label: PHASE_LABEL[key],
    dateLabel: formatDayShort(dateFor(key)),
    state:
      index < currentIndex
        ? "complete"
        : index === currentIndex
          ? "current"
          : "upcoming",
  }));

  return { status: data.status, steps };
}
